import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { TranslatePipe } from '@ngx-translate/core';

import { CurrencyService } from '../../../core/services/currency.service';
import { VariantAttributeService } from '../../../core/services/variant-attribute.service';
import {
  ProductVariantMatrixDto,
  VariantAttributeDto,
  VariantAttributeValueDto,
} from '../../../core/models/variant.models';

/** Bitta o'q (atribut) + tanlangan qiymatlar. */
interface Axis {
  attributeId: string;
  name: string;
  values: VariantAttributeValueDto[];
  selected: VariantAttributeValueDto[];
  newValue?: string;
  showNewValue?: boolean;
}

/** Kartezian kombinatsiya qatori — parentga shu yuboriladi. */
export interface VariantCombo {
  key: string;
  /** Mavjud variant id (edit rejimida); yangi qatorda null. */
  variantId: string | null;
  valueIds: string[];
  label: string;
  quantity: number;
  /** Edit rejimida mavjud variant qoldig'i (faqat ko'rsatish uchun). */
  currentStock: number | null;
  /** Variantsiz mahsulotning default qoldig'i shu kombinatsiyaga o'tadi (birinchi kombinatsiya). */
  isDefaultTarget: boolean;
  price: number | null;
  purchasePrice: number | null;
}

@Component({
  selector: 'app-variant-builder',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    MultiSelectModule,
    TagModule,
    TooltipModule,
    TranslatePipe,
  ],
  templateUrl: './variant-builder.component.html',
})
export class VariantBuilderComponent implements OnInit {
  private attrSvc = inject(VariantAttributeService);
  private readonly destroyRef = inject(DestroyRef);
  readonly currencySvc = inject(CurrencyService);

  /** Edit rejimida mavjud variant matritsasi. Berilmasa — create (bo'sh boshlanadi). */
  @Input() initialMatrix: ProductVariantMatrixDto | null = null;
  /** Yangi qiymat/tur yaratganda filtrlash uchun (ixtiyoriy). */
  @Input() storeTypeId: string | null = null;
  /** Kombinatsiyalarda miqdor ustuni ko'rsatilsinmi (create da ha, edit da yo'q — qoldiq alohida). */
  @Input() showQuantity = true;
  /**
   * Qator narxlari qaysi valyutada kiritiladi. Kiritish aniqligi shundan olinadi —
   * berilmasa tenant kotirovka valyutasi ishlatiladi.
   */
  @Input() sellCurrency: string | null = null;
  @Input() purchaseCurrency: string | null = null;

  @Output() combosChange = new EventEmitter<VariantCombo[]>();
  @Output() attributeIdsChange = new EventEmitter<string[]>();

  /** Butun lug'atdagi variant atributlari. */
  allAttributes = signal<VariantAttributeDto[]>([]);
  axes = signal<Axis[]>([]);
  combos = signal<VariantCombo[]>([]);

  /** Variantsiz mahsulotning default variant qoldig'i (birinchi kombinatsiyaga o'tadi). */
  private defaultStock: number | null = null;

  /** "Tur tanlang" dropdown modeli. */
  newAxisAttrId: string | null = null;

  // Inline yangi tur yaratish
  showNewAttr = false;
  newAttrName = '';
  newAttrValuesText = '';
  savingAttr = signal(false);

  /** Hali o'q sifatida qo'shilmagan atributlar. */
  availableAttributes = computed(() => {
    const used = new Set(this.axes().map((a) => a.attributeId));
    return this.allAttributes().filter((a) => !used.has(a.id));
  });

  totalQty = computed(() => this.combos().reduce((s, c) => s + (c.quantity || 0), 0));

  ngOnInit(): void {
    this.attrSvc
      .getAll(undefined, false, this.storeTypeId ?? undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((r) => {
        this.allAttributes.set(r.data ?? []);
        if (this.initialMatrix) this.hydrateFromMatrix(this.initialMatrix);
      });
  }

  /** Edit: matritsadan o'qlar va mavjud variant qatorlarini tiklaydi. */
  private hydrateFromMatrix(m: ProductVariantMatrixDto): void {
    // O'qlar: matritsa atributlari, har biridagi barcha qiymatlar tanlangan holda.
    const axes: Axis[] = m.attributes.map((a) => {
      const values: VariantAttributeValueDto[] = a.values.map((v) => ({
        id: v.valueId,
        value: v.value,
        extra: v.extra,
        sortOrder: v.sortOrder,
        isActive: true,
      }));
      return { attributeId: a.attributeId, name: a.attributeName, values, selected: [...values] };
    });
    this.axes.set(axes);

    // Variantsiz mahsulot (default variant, values yo'q) — uning qoldig'ini
    // saqlaymiz; u birinchi yangi kombinatsiyaga o'tadi (backend ham shunday qiladi).
    const defaultV = m.variants.find((v) => v.values.length === 0);
    this.defaultStock = defaultV?.stock ?? null;

    // Mavjud variantlar → kombinatsiya qatorlari (id bilan).
    const combos: VariantCombo[] = m.variants
      .filter((v) => v.values.length > 0)
      .map((v) => {
        const valueIds = v.values.map((x) => x.valueId);
        return {
          key: [...valueIds].sort().join(':'),
          variantId: v.id,
          valueIds,
          label: v.values.map((x) => x.value).join(' / '),
          quantity: v.stock ?? 0,
          currentStock: v.stock,
          isDefaultTarget: false,
          price: v.sellPriceOverride,
          purchasePrice: v.purchasePriceOverride,
        };
      });
    this.combos.set(combos);
    this.emit();
  }

  /** Tanlangandan keyin darhol o'q sifatida qo'shadi. */
  addAxis(): void {
    const id = this.newAxisAttrId;
    if (!id) return;
    const attr = this.allAttributes().find((a) => a.id === id);
    if (!attr) return;
    this.axes.update((list) => [
      ...list,
      { attributeId: attr.id, name: attr.name, values: attr.values, selected: [] },
    ]);
    setTimeout(() => (this.newAxisAttrId = null));
    this.recompute();
  }

  removeAxis(attributeId: string): void {
    this.axes.update((list) => list.filter((a) => a.attributeId !== attributeId));
    this.recompute();
  }

  /** Inline: o'qqa yangi qiymat (masalan 4XL) qo'shadi. */
  addValueToAxis(axis: Axis): void {
    const value = (axis.newValue ?? '').trim();
    if (!value) return;
    this.attrSvc
      .addValue(axis.attributeId, { value, sortOrder: axis.values.length })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.attrSvc
          .getAll(undefined, false, this.storeTypeId ?? undefined)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((r) => {
            const all = r.data ?? [];
            this.allAttributes.set(all);
            const fresh = all.find((a) => a.id === axis.attributeId);
            if (fresh) {
              this.axes.update((list) =>
                list.map((a) =>
                  a.attributeId === axis.attributeId
                    ? { ...a, values: fresh.values, newValue: '', showNewValue: false }
                    : a,
                ),
              );
            }
          });
      });
  }

  /** Inline: yangi tur (atribut) yaratadi va darhol o'q qiladi. */
  createAttributeInline(): void {
    const name = this.newAttrName.trim();
    const values = this.newAttrValuesText
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
    if (!name || values.length === 0) return;

    this.savingAttr.set(true);
    this.attrSvc
      .create({
        name,
        values: values.map((v, i) => ({ value: v, sortOrder: i })),
        storeTypeId: this.storeTypeId,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const newId = res.data?.id;
          this.attrSvc
            .getAll(undefined, false, this.storeTypeId ?? undefined)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((r) => {
              const all = r.data ?? [];
              this.allAttributes.set(all);
              const created = all.find((a) => a.id === newId) ?? all.find((a) => a.name === name);
              if (created) {
                this.axes.update((list) => [
                  ...list,
                  {
                    attributeId: created.id,
                    name: created.name,
                    values: created.values,
                    selected: [...created.values],
                  },
                ]);
                this.recompute();
              }
              this.showNewAttr = false;
              this.newAttrName = '';
              this.newAttrValuesText = '';
              this.savingAttr.set(false);
            });
        },
        error: () => this.savingAttr.set(false),
      });
  }

  /** O'q qiymatlari o'zgarganda — kartezian kombinatsiyalarni qayta hisoblaydi. */
  recompute(): void {
    const activeAxes = this.axes().filter((a) => a.selected.length > 0);
    if (activeAxes.length === 0) {
      this.combos.set([]);
      this.emit();
      return;
    }
    let cartesian: VariantAttributeValueDto[][] = [[]];
    for (const axis of activeAxes) {
      cartesian = cartesian.flatMap((c) => axis.selected.map((v) => [...c, v]));
    }
    const prev = new Map(this.combos().map((c) => [c.key, c]));
    const newCombos = cartesian.map((combo) => {
      const valueIds = combo.map((v) => v.id);
      const key = [...valueIds].sort().join(':');
      const existing = prev.get(key);
      return {
        key,
        variantId: existing?.variantId ?? null,
        valueIds,
        label: combo.map((v) => v.value).join(' / '),
        quantity: existing?.quantity ?? (this.showQuantity ? 1 : 0),
        currentStock: existing?.currentStock ?? null,
        isDefaultTarget: false,
        price: existing?.price ?? null,
        purchasePrice: existing?.purchasePrice ?? null,
      };
    });

    // Variantsiz mahsulot edit'da: default qoldiq birinchi YANGI kombinatsiyaga
    // o'tadi (backend newInputs[0] ni default'ga aylantiradi). Uni belgilab,
    // qoldig'ini disable holatda ko'rsatamiz.
    if (this.defaultStock != null) {
      const firstNew = newCombos.find((c) => c.variantId == null);
      if (firstNew) {
        firstNew.isDefaultTarget = true;
        firstNew.currentStock = this.defaultStock;
      }
    }

    this.combos.set(newCombos);
    this.emit();
  }

  /** Narx/miqdor inputlari o'zgarganda parentga yetkazadi. */
  emit(): void {
    this.combosChange.emit(this.combos());
    this.attributeIdsChange.emit(this.axes().map((a) => a.attributeId));
  }
}
